import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2 } from 'lucide-react'
import { authApi } from '@/lib/auth.api'
import { merkeFirma } from '@/lib/firma'
import { verlangtEinrichtung } from '@/lib/zwei-faktor'
import type { Mitgliedschaft } from '@/lib/auth.types'

/**
 * AP-38-B — **Für welche Firma arbeite ich gerade?**
 *
 * Steht oben in der Seitenleiste, aber nur, wenn die Person für mindestens zwei
 * Firmen arbeitet — bei allen anderen gibt es nichts zu wählen. Die aktive Firma
 * steht immer lesbar da, eingeklappt als Kürzel: Wer für zehn Kunden arbeitet,
 * soll nie im falschen Mandanten erfassen.
 *
 * Nach dem Wechsel lädt die Seite **neu** und öffnet die Startseite. Ein Wechsel
 * ohne Neuladen müsste jeden Zwischenspeicher auf Modulebene kennen
 * (Terminologie, PDF-Ausgabe, Listen-Layouts, Firmenfarbe) — einer, der
 * vergessen wird, zeigte Daten der vorigen Firma.
 */
export function FirmenAuswahl({ collapsed, onExpand }: { collapsed: boolean; onExpand: () => void }) {
  const { t } = useTranslation()
  const [firmen, setFirmen] = useState<Mitgliedschaft[]>([])
  const [wechselt, setWechselt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let aktiv = true
    authApi
      .memberships()
      .then((res) => {
        if (aktiv) setFirmen(res.data.data)
      })
      .catch(() => {
        // Ohne Liste keine Auswahl — die Arbeit in der aktuellen Firma geht weiter.
      })
    return () => {
      aktiv = false
    }
  }, [])

  if (firmen.length < 2) return null
  const aktuell = firmen.find((f) => f.aktuell) ?? firmen[0]

  async function wechsle(tenantId: string) {
    if (tenantId === aktuell.tenantId) return
    setWechselt(true)
    setFehler(null)
    try {
      await authApi.switchFirma(tenantId)
      merkeFirma(tenantId)
      window.location.assign('/')
    } catch (err) {
      // AP-38-E: Nach der Frist (oder nach Annahme eines externen Zugangs) verlangt
      // der Wechsel die Einrichtung — dann geht es dorthin.
      if (verlangtEinrichtung(err)) return
      setWechselt(false)
      setFehler(t('firma.wechselFehler'))
    }
  }

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onExpand}
        className="mx-2 mt-2 flex h-9 items-center justify-center rounded-md text-xs font-semibold"
        style={{ backgroundColor: 'var(--sidebar-active-bg)', color: 'var(--sidebar-active-fg)' }}
        title={`${t('firma.label')}: ${aktuell.name}`}
        aria-label={`${t('firma.label')}: ${aktuell.name}`}
      >
        {kuerzel(aktuell.name)}
      </button>
    )
  }

  return (
    <div className="px-3 pt-3">
      <label
        htmlFor="firmen-auswahl"
        className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide opacity-70"
      >
        <Building2 size={12} aria-hidden="true" />
        {t('firma.label')}
      </label>
      <select
        id="firmen-auswahl"
        value={aktuell.tenantId}
        disabled={wechselt}
        onChange={(e) => wechsle(e.target.value)}
        className="w-full rounded-md border px-2 py-1.5 text-sm font-medium"
        style={{
          backgroundColor: 'var(--sidebar-active-bg)',
          color: 'var(--sidebar-active-fg)',
          borderColor: 'var(--sidebar-border)',
        }}
      >
        {firmen.map((f) => (
          <option key={f.tenantId} value={f.tenantId} style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
            {f.name}
          </option>
        ))}
      </select>
      {fehler && <p className="mt-1 text-xs text-red-500">{fehler}</p>}
    </div>
  )
}

/** Zwei Buchstaben für die eingeklappte Leiste: „Muster Bau GmbH" → „MB". */
export function kuerzel(name: string): string {
  const woerter = name
    .replace(/\b(GmbH|AG|KG|OG|e\.U\.|GesmbH|Co)\b\.?/gi, '')
    .split(/[\s&+-]+/)
    .filter(Boolean)
  if (woerter.length === 0) return name.slice(0, 2).toUpperCase()
  if (woerter.length === 1) return woerter[0].slice(0, 2).toUpperCase()
  return (woerter[0][0] + woerter[1][0]).toUpperCase()
}
